/**
 * Net Proceeds Waterfall Component
 *
 * Visualizes the flow from purchase price to net proceeds showing:
 * - Purchase price
 * - Working capital adjustments
 * - Transaction costs
 * - Debt payoff
 * - Escrow holdback
 * - Taxes
 * - Final net proceeds
 */

import React from 'react';

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(amount);
};

function NetProceedsWaterfall({ netProceedsAnalysis }) {
  if (!netProceedsAnalysis) return null;

  const { summary, waterfall, transactionCosts, taxAnalysis, escrow } = netProceedsAnalysis;

  // Calculate max value for bar scaling
  const maxValue = Math.max(...waterfall.map(item => Math.abs(item.amount)));

  return (
    <div className="net-proceeds-waterfall">
      <h2>Net Proceeds Analysis</h2>
      <p className="section-description">
        Detailed breakdown from purchase price to your net proceeds
      </p>

      {/* Summary Cards */}
      <div className="summary-cards">
        <div className="summary-card">
          <span className="card-label">Purchase Price</span>
          <span className="card-value">{formatCurrency(summary.purchasePrice)}</span>
        </div>
        <div className="summary-card highlight">
          <span className="card-label">Net Proceeds</span>
          <span className="card-value">{formatCurrency(summary.netProceeds)}</span>
        </div>
        <div className="summary-card">
          <span className="card-label">Retention Rate</span>
          <span className="card-value">{summary.retentionRate}%</span>
        </div>
        <div className="summary-card">
          <span className="card-label">Total Deductions</span>
          <span className="card-value negative">
            {formatCurrency(summary.totalDeductions)}
          </span>
        </div>
      </div>

      {/* Waterfall Chart */}
      <div className="waterfall-chart">
        <h3>Proceeds Waterfall</h3>
        <div className="waterfall-items">
          {waterfall.map((item, index) => {
            const isPositive = item.amount >= 0;
            const isSubtotal = item.isSubtotal;
            const isFinal = item.isFinal;
            const barWidth = (Math.abs(item.amount) / maxValue) * 100;

            return (
              <div
                key={index}
                className={`waterfall-item ${isSubtotal ? 'subtotal' : ''} ${isFinal ? 'final' : ''} ${isPositive ? 'positive' : 'negative'}`}
              >
                <div className="item-label">{item.item}</div>
                <div className="item-bar-container">
                  <div
                    className={`item-bar ${isPositive ? 'positive' : 'negative'}`}
                    style={{ width: `${barWidth}%` }}
                  ></div>
                </div>
                <div className={`item-value ${isPositive ? '' : 'negative'}`}>
                  {isPositive ? '' : '-'}
                  {formatCurrency(Math.abs(item.amount))}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Transaction Costs Breakdown */}
      <div className="costs-breakdown">
        <h3>Transaction Costs Detail</h3>
        <div className="costs-table">
          <div className="cost-row">
            <span>Investment Banker Fee</span>
            <span>{formatCurrency(transactionCosts.investmentBankerFee)}</span>
          </div>
          <div className="cost-row">
            <span>Legal Fees</span>
            <span>{formatCurrency(transactionCosts.legalFees)}</span>
          </div>
          <div className="cost-row">
            <span>Accounting Fees</span>
            <span>{formatCurrency(transactionCosts.accountingFees)}</span>
          </div>
          <div className="cost-row">
            <span>Other Advisors</span>
            <span>{formatCurrency(transactionCosts.otherAdvisors)}</span>
          </div>
          <div className="cost-row total">
            <span>Total Transaction Costs</span>
            <span>{formatCurrency(transactionCosts.total)}</span>
          </div>
        </div>
      </div>

      {/* Tax Breakdown */}
      <div className="tax-breakdown">
        <h3>Tax Analysis</h3>
        <div className="tax-table">
          {Object.entries(taxAnalysis.taxes || {}).map(([taxType, amount]) => (
            <div className="tax-row" key={taxType}>
              <span>{formatTaxType(taxType)}</span>
              <span>{formatCurrency(amount)}</span>
            </div>
          ))}
          <div className="tax-row total">
            <span>Total Taxes</span>
            <span>{formatCurrency(taxAnalysis.totalTax)}</span>
          </div>
          <div className="tax-row effective">
            <span>Effective Tax Rate</span>
            <span>{taxAnalysis.effectiveRate}%</span>
          </div>
        </div>
      </div>

      {/* Escrow Information */}
      {escrow && escrow.holdback > 0 && (
        <div className="escrow-info">
          <h3>Escrow Holdback</h3>
          <div className="escrow-details">
            <div className="escrow-row">
              <span>Amount Held</span>
              <span>{formatCurrency(escrow.holdback)}</span>
            </div>
            <div className="escrow-row">
              <span>Expected Release</span>
              <span>{formatCurrency(escrow.expectedRelease)}</span>
            </div>
            <div className="escrow-row">
              <span>Typical Timeline</span>
              <span>{escrow.releaseTimeline}</span>
            </div>
          </div>
          <p className="escrow-note">
            Note: Escrow holdbacks are typically released after the representation and warranty period expires,
            subject to any claims.
          </p>
        </div>
      )}

      {/* Visual Summary */}
      <div className="visual-summary">
        <h3>Where Your Proceeds Go</h3>
        <div className="donut-chart">
          <svg viewBox="0 0 100 100">
            {/* Simplified pie chart segments */}
            <circle
              cx="50" cy="50" r="40"
              fill="none"
              stroke="#10b981"
              strokeWidth="20"
              strokeDasharray={`${summary.retentionRate * 2.51} 251`}
              strokeDashoffset="0"
            />
            <circle
              cx="50" cy="50" r="40"
              fill="none"
              stroke="#ef4444"
              strokeWidth="20"
              strokeDasharray={`${((100 - summary.retentionRate) * (taxAnalysis.totalTax / (summary.purchasePrice - summary.netProceeds))) * 2.51} 251`}
              strokeDashoffset={`${-summary.retentionRate * 2.51}`}
            />
            <circle
              cx="50" cy="50" r="40"
              fill="none"
              stroke="#f59e0b"
              strokeWidth="20"
              strokeDasharray={`${((100 - summary.retentionRate) * (1 - taxAnalysis.totalTax / (summary.purchasePrice - summary.netProceeds))) * 2.51} 251`}
              strokeDashoffset={`${-summary.retentionRate * 2.51 - ((100 - summary.retentionRate) * (taxAnalysis.totalTax / (summary.purchasePrice - summary.netProceeds))) * 2.51}`}
            />
          </svg>
          <div className="chart-center">
            <span className="center-value">{summary.retentionRate}%</span>
            <span className="center-label">Net</span>
          </div>
        </div>
        <div className="chart-legend">
          <div className="legend-item">
            <span className="legend-color net"></span>
            <span>Net Proceeds ({summary.retentionRate}%)</span>
          </div>
          <div className="legend-item">
            <span className="legend-color taxes"></span>
            <span>Taxes</span>
          </div>
          <div className="legend-item">
            <span className="legend-color costs"></span>
            <span>Transaction Costs & Other</span>
          </div>
        </div>
      </div>
    </div>
  );
}

function formatTaxType(type) {
  const types = {
    corporateTax: 'Corporate Tax',
    dividendTax: 'Dividend Tax',
    niit: 'NIIT (3.8%)',
    stateTax: 'State Tax',
    ordinaryIncomeTax: 'Ordinary Income Tax',
    capitalGainsTax: 'Capital Gains Tax'
  };
  return types[type] || type;
}

export default NetProceedsWaterfall;
